import React from 'react';
import { ErrorStateProps, ErrorStateSize } from '../../../types/DesignSystem/ErrorState';
import Typography from '../Typography/Typography';
import { AlertTriangle } from '../Iconography/Icons';
import { withClassName } from '../cn';

/**
 * ErrorState — the third of the four observable states, given a component.
 *
 * **Why it exists.** `okf/e2e-template.md` requires every module to document loading /
 * empty / error / success. `Spinner` covers loading and `EmptyState` covers empty; error
 * had nothing, so the app's only error surface was react-router's development fallback —
 * a black-on-white stack trace headed *"Unexpected Application Error!"* with a note to
 * the developer about `errorElement`. That page is a debugging tool, and shipping it is
 * how a user ends up reading `react_stack_bottom_frame` and filing no bug report.
 *
 * This is deliberately **router-agnostic**: it takes strings and nodes, never a
 * `useRouteError()` result. The design system must not know what a route is — see
 * `__tests__/dependencyDirection.test.ts`. The caller unwraps the error and passes the
 * two things a user needs (what failed, what to do) plus the one a developer needs
 * (`detail`).
 *
 * Copy guidance, because this is where it gets decided: name the failure in the user's
 * words, give one next step, and put the machine text in `detail`. No apologies — an
 * error message that spends its first line saying sorry has spent it saying nothing.
 */

const sizing: Record<ErrorStateSize, { pad: string; gap: string; icon: number }> = {
  sm: { pad: 'tw-py-8 tw-px-4', gap: 'tw-gap-1', icon: 20 },
  md: { pad: 'tw-py-16 tw-px-6', gap: 'tw-gap-2', icon: 32 },
};

const ErrorState = React.forwardRef<HTMLDivElement, ErrorStateProps>(function ErrorState(
  props,
  ref,
) {
  const {
    title = 'Something went wrong',
    description,
    detail,
    action,
    icon,
    size = 'md',
    role,
    className = '',
    'data-testid': dataTestId,
    ...rest
  } = props;
  /*
    `'role' in props`, NOT a `role = 'alert'` default parameter.

    A default parameter fires whenever the caller passes `undefined`, so
    `role={undefined}` would silently restore `'alert'` and there would be no way to opt
    out — which matters, because nesting this inside an existing live region makes a
    screen reader announce twice. The prop docs claimed opting out worked; it did not,
    and the test written for it is what caught the lie.

    This is the same trap `CLAUDE.md` records for a test helper (`makeAppContext(channelKey
    = REAL_KEY)` quietly testing the selected case). It is worth knowing that it applies
    to component props exactly as readily.
  */
  const resolvedRole = 'role' in props ? role : 'alert';
  const { pad, gap, icon: iconSize } = sizing[size];
  const testId = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

  return (
    <div
      {...rest}
      ref={ref}
      role={resolvedRole}
      className={withClassName(`tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center ${pad} ${gap}`, className)}
      data-testid={dataTestId}
    >
      {/* `aria-hidden` on the glyph: the title is the accessible name, and a triangle
          announced as "warning" before it just delays the sentence that matters. The
          default icon is supplied rather than optional — unlike `EmptyState`, where
          absence is unremarkable, an error benefits from being scannable in a page of
          chrome. */}
      <span
        className="tw-text-tone-danger-glyph tw-mb-1"
        aria-hidden="true"
        data-testid={testId('icon')}
      >
        {icon ?? <AlertTriangle size={iconSize} />}
      </span>

      {/*
        `grey-900`, where `EmptyState` uses `grey-700`. An empty state is a quiet moment
        and should not shout; an error is the one thing on the surface the reader needs,
        so it gets full ink. The RED is spent on the icon alone — red display text at
        heading size reads as alarm rather than information, and `red-error-900` on white
        is 6.71:1, so it would also be the loudest thing on the page.
      */}
      {/* § 275 — a failed panel is a moment: `heading-3` (16/600), not the 14/600 a
          column header wears. See Dialog for the full reasoning. */}
      <Typography variant="heading-3" className="tw-text-ink" data-testid={testId('title')}>
        {title}
      </Typography>

      {description && (
        <Typography variant="body-primary" className="tw-text-ink-secondary" data-testid={testId('description')}>
          {description}
        </Typography>
      )}

      {action && <div className="tw-mt-4" data-testid={testId('action')}>{action}</div>}

      {detail && (
        /*
          A native `<details>`, not a state hook: keyboard-operable, disclosed to AT as
          an expandable, and correct with no JavaScript. Closed by default — its content
          is therefore not rendered, which is also why putting it inside `role="alert"`
          does not read the stack trace aloud.
        */
        <details className="tw-mt-4 tw-w-full tw-max-w-xl tw-text-left" data-testid={testId('detail')}>
          <summary
            /* § 295 — the hover fades (the ladder's `fast` rung); it snapped. */
            className="tw-cursor-pointer tw-text-caption-metadata tw-text-ink-tertiary hover:tw-text-ink motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift"
            data-testid={testId('detail-toggle')}
          >
            Technical details
          </summary>
          {/* `overflow-x-auto` on its own container: a stack trace is the widest thing on
              any page, and it must scroll inside this box rather than making the route
              scroll sideways. */}
          <pre
            className="tw-mt-2 tw-overflow-x-auto tw-rounded-ctl tw-bg-surface-sunken tw-p-3 tw-text-caption-metadata tw-text-ink-secondary"
            data-testid={testId('detail-body')}
          >
            {detail}
          </pre>
        </details>
      )}
    </div>
  );
});

export default ErrorState;
